import * as React from "react";
import { useNavigate } from "react-router-dom";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Card,
  FormControl,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SoftBox from "components/SoftBox";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useSelector } from "react-redux";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { RootState } from "store";
import { AxiosError } from "axios";
import SoftTypography from "components/SoftTypography";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

interface Divisions {
  division_id: number;
  division_name: string;
}

interface AssetTypeOption {
  asset_type_id: number;
  asset_type_name: string;
}

// Table options
const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

// Extracted component for asset name cell
interface AssetNameCellProps {
  assetName: string;
}

const AssetNameCell: React.FC<AssetNameCellProps> = ({ assetName }) => {
  const displayValue =
    assetName?.length > 20 ? `${assetName.substring(0, 20)}...` : assetName;

  return (
    <Tooltip title={assetName}>
      <span>{displayValue}</span>
    </Tooltip>
  );
};

// Extracted component for address cell
interface AddressCellProps {
  address: string;
  city: string;
  state: string;
  addressType: string;
}

const AddressCell: React.FC<AddressCellProps> = ({
  address,
  city,
  state,
  addressType,
}) => {
  const addressData = `${address}, ${city}, ${state},${addressType}`;
  const displayValue =
    addressData?.length > 20
      ? `${addressData.substring(0, 20)}...`
      : addressData;

  return (
    <Tooltip title={addressData}>
      <span>{displayValue}</span>
    </Tooltip>
  );
};

// Extracted component for action buttons
interface ActionButtonsCellProps {
  assetId: string;
  onNavigate: (assetId: string) => void;
}

const ActionButtonsCell: React.FC<ActionButtonsCellProps> = ({
  assetId,
  onNavigate,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <IconButton aria-label="view" onClick={() => onNavigate(assetId)}>
        <VisibilityIcon />
      </IconButton>
    </Box>
  );
};

const CustomerAssetsList = () => {
  const navigate = useNavigate();
  const [divisionsFilter, setDivisionsFilter] = React.useState<number | null>(
    null
  );
  const [assetTypeFilter, setAssetTypeFilter] = React.useState<AssetTypeOption | null>(null);
  const [divisions, setDivisions] = React.useState<Divisions[]>([]);
  const [assetTypes, setAssetTypes] = React.useState<AssetTypeOption[]>([]);
  const [rows, setRows] = React.useState([]);
  const [error, setError] = React.useState<AxiosError | null>(null);
  const user = useSelector((state: RootState) => state.auth.user);

  const fetchDivisions = async () => {
    try {
      const response = await axios.get(`/divisions/customer/${user?.userId}`);
      setDivisions(response?.data?.divisions);
    } catch (error) {
      console.error("Error fetching statuses:", error);
    }
  };

  const fetchAssetTypes = React.useCallback(async () => {
    try {
      const res = await axios.get("/asset-types");
      const list = res.data?.list ?? [];
      setAssetTypes(
        list.map((a: AssetTypeOption) => ({
          asset_type_id: a.asset_type_id,
          asset_type_name: a.asset_type_name ?? "",
        }))
      );
    } catch (e) {
      console.error("Failed to fetch asset types", e);
    }
  }, []);

  React.useEffect(() => {
    fetchDivisions();
  }, []);

  React.useEffect(() => {
    fetchAssetTypes();
  }, [fetchAssetTypes]);

  // Fixed: Render functions for custom cells
  const renderAssetNameCell = (value: any, tableMeta: any) => {
    const { currentTableData, rowIndex } = tableMeta;
    const data = tableMeta?.tableData[currentTableData[rowIndex]?.index];
    const assetName = data?.asset_name;

    return <AssetNameCell assetName={assetName} />;
  };

  const renderAddressCell = (value: any, tableMeta: any) => {
    const { currentTableData, rowIndex } = tableMeta;
    const data = tableMeta?.tableData[currentTableData[rowIndex]?.index];

    return (
      <AddressCell
        address={value}
        city={data?.city_name}
        state={data?.state_name}
        addressType={data?.address_type}
      />
    );
  };

  const renderActionsCell = (value: any, tableMeta: any) => {
    const { currentTableData, rowIndex } = tableMeta;
    const data = tableMeta?.tableData[currentTableData[rowIndex]?.index];

    return (
      <ActionButtonsCell
        assetId={data?.asset_id}
        onNavigate={(assetId) => navigate(`/customer/assets-view/${assetId}`)}
      />
    );
  };

  const columns: MUIDataTableColumn[] = [
    { name: "asset_service_id", label: "Asset Id" },
    ...(user?.type === "company" && user?.division_id === null
      ? [{ name: "division_name", label: "Division" }]
      : []),
    {
      name: "asset_name",
      label: "Brand Name",
      options: {
        filter: false,
        customBodyRender: renderAssetNameCell,
      },
    },
    { name: "asset_sku", label: "SKU" },
    { name: "asset_type_name", label: "Asset Type" },
    {
      name: "address",
      label: "Address",
      options: {
        filter: false,
        customBodyRender: renderAddressCell,
      },
    },
    {
      name: "id",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: renderActionsCell,
      },
    },
  ];

  const fetchData = React.useMemo(
    () => async () => {
      try {
        const response = await axios.get(`/assets/customer/${user?.userId}`);
        let assets = response?.data?.list;
        if (divisionsFilter) {
          assets = assets.filter(
            (asset: { division_id: number }) =>
              asset.division_id === divisionsFilter
          );
        }
        if (assetTypeFilter) {
          assets = assets.filter(
            (asset: { asset_type_id: number }) =>
              asset.asset_type_id === assetTypeFilter.asset_type_id
          );
        }
        setRows(assets);
      } catch (error) {
        if (error instanceof AxiosError && error) {
          setError(error);
        }
      }
    },
    [user, divisionsFilter, assetTypeFilter]
  );

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1.5, mb: 2 }}>
          <FormControl sx={{ width: 200, minWidth: 200 }}>
            <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5 }}>
              Asset Type
            </SoftTypography>
            <Autocomplete
              options={assetTypes}
              getOptionLabel={(o) => o.asset_type_name}
              value={assetTypeFilter}
              onChange={(_, v) => setAssetTypeFilter(v)}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" placeholder="All" />
              )}
            />
          </FormControl>
          {user?.type === "company" && user?.division_id === null && (
            <FormControl sx={{ width: 200, minWidth: 200 }}>
              <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5 }}>
                Divisions
              </SoftTypography>
              <Autocomplete
                options={divisions}
                getOptionLabel={(option) => option.division_name}
                value={divisions?.find((d) => d.division_id === divisionsFilter) ?? null}
                onChange={(event, value) => {
                  setDivisionsFilter(value?.division_id ?? null);
                }}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" size="small" placeholder="All" />
                )}
              />
            </FormControl>
          )}
        </Box>
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="manage-assets"
        >
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={"Manage Assets"}
              data={rows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
          {error && <div>Error: {error.message}</div>}
        </SoftBox>
      </Card>
    </DashboardLayout>
  );
};

export default CustomerAssetsList;
